/** @jsxImportSource @emotion/react */
import styled, { Interpolation } from "@emotion/styled";
import { Spacer } from "../../general/Spacer/Spacer";
import { Link } from "react-router-dom";
import { NavbarButtonOptions } from "../NavbarV2Desktop";
import { useMemo, useRef } from "react";
import { useHover } from "usehooks-ts";
import { useActive } from "hooks/useActive";
import { Theme } from "@emotion/react";
import { DeviceType, TABLET_WIDTH, useDeviceType } from "hooks/useDeviceType";
import { GlobalStore } from "../../../store/global/GlobalStore";
import { Text } from "components/general/Text";

export interface NavbarButtonProps extends NavbarButtonOptions {
  onClick?: () => void;
  css?: Interpolation<Theme>;
  isAdditionalHover?: boolean;
}

export const NavbarButton = ({
  tokenAddress,
  text,
  pathName,
  colors,
  css,
  onClick,
  isAdditionalHover,
}: NavbarButtonProps) => {
  const ref = useRef<HTMLButtonElement>(null);
  const isHover = useHover(ref) || isAdditionalHover;
  const isActive = useActive(ref);
  const currentPathName = window.location.pathname;
  const isSelected = currentPathName === pathName;
  const Icon = isSelected || isHover ? tokenAddress.active : tokenAddress.default;
  const isTablet = useDeviceType() === DeviceType.Tablet;

  const iconSize = useMemo(() => {
    if (isActive) return 26;
    if (isHover) return 30;
    return 28;
  }, [isActive, isHover]);
  return (
    <Link
      to={pathName ?? "#"}
      onClick={() => {
        GlobalStore.update(s => {
          s.modalSelector = undefined;
        });
        onClick?.();
      }}
      style={{ width: "fit-content" }}
    >
      <NavbarButtonContainer isSelected={isSelected} colors={colors} ref={ref} css={css}>
        <Icon width={iconSize} height={iconSize} />
        {!isTablet && (
          <>
            <Spacer width="10px" />
            <Text textStyle="Title" size="Small">
              {text}
            </Text>
          </>
        )}
      </NavbarButtonContainer>
    </Link>
  );
};

// Sub-components

interface NavbarButtonContainerProps {
  isSelected: boolean;
  colors: NavbarButtonOptions["colors"];
}

export const NavbarButtonContainer = styled.button<NavbarButtonContainerProps>`
  background: none;
  border: none;
  border-radius: 14px;
  width: 158px;
  height: 32px;
  text-align: left;
  display: flex;
  flex-direction: row;
  align-items: center;
  padding: 0px;
  color: ${props =>
    props.isSelected
      ? props.colors?.selected
      : props.colors?.default ?? props.theme.font.primary.default};
  transition:
    color 0.1s linear,
    transform 0.1s linear;

  &:hover {
    color: ${props => props.colors?.hover};
    transform: scale(1.02);
  }

  &:active {
    color: ${props => props.colors?.active};
    transform: scale(0.98);
  }

  @media (max-width: ${TABLET_WIDTH}px) {
    width: fit-content;
  }
`;
